import { firstValueFrom } from 'rxjs';
import {
  decodeConversationCompactionSystemNoticePayload,
  decodeThoughtTraceDataSnapshot,
  isCanonicalTimestamp,
  isCanonicalUuid,
  mergeThoughtTraceProgressData,
} from '@automatick/shared-client-ts';
import {
  ChatSessionThoughtTraceEntryResponse,
  ChatSessionThoughtTraceSummaryResponse,
  PresentationEnvelopeContract,
} from '@automatick/shared-ng';
import {
  ConversationPulseStage,
  ConversationPulseKind,
  RealtimeRenderDisplayKind,
  RealtimeRenderDisplayStageLabel,
  RealtimeRenderState,
  RealtimeRenderEventType,
  RealtimeRenderStage,
  type JsonValue,
  type RealtimeRenderEventStage,
} from '@automatick/shared-ts';
import { WebChatMessageRuntime } from './web-chat-message.runtime';
import { WebChatStateStore } from './web-chat-state.store';
import {
  WebChatCanonicalRenderEvent,
  WebChatRenderEvent,
  WebChatState,
  WebChatThoughtTraceSummary,
} from './web-chat.models';
import {
  decodeWebChatPresentations,
  decodeWebChatPulseMetadata,
  isWebChatRealtimeAudienceScope,
  isWebChatRealtimeRenderSeverity,
  isWebChatRealtimeRenderState,
  isWebChatRenderDisplayKind,
  isWebChatRenderDisplayStageLabel,
  isWebChatRenderEventStage,
  isWebChatRenderEventType,
  normalizeWebChatPresentations,
} from './web-chat-contract-normalizers';

export class WebChatThoughtTraceRuntime {
  constructor(
    private readonly state: WebChatStateStore,
    private readonly messages: WebChatMessageRuntime,
  ) {}

  normalizePresentations(data: unknown): PresentationEnvelopeContract[] {
    return normalizeWebChatPresentations(data);
  }

  normalizeThoughtTraceSummary(
    trace: ChatSessionThoughtTraceSummaryResponse | null | undefined,
  ): WebChatThoughtTraceSummary | null {
    const thoughtTraceId = readRequiredUuid(trace?.thoughtTraceId);
    const sessionId = readRequiredUuid(trace?.sessionId);
    const workspaceId = readRequiredUuid(trace?.workspaceId);
    const requestedByUserId = readNullableUuid(trace?.requestedByUserId);
    const workRequestId = readNullableUuid(trace?.workRequestId);
    const correlationId = readNullableUuid(trace?.correlationId);
    const streamId = readExactNonemptyString(trace?.streamId);
    const audienceScope = trace?.audienceScope;
    const latestStage = trace?.latestStage;
    const latestState = trace?.latestState;
    const latestEventType = trace?.latestEventType;
    const latestMessage = readExactNonemptyString(trace?.latestMessage);
    const latestDisplayKind = trace?.latestDisplayKind;
    const latestDisplayStageLabel = trace?.latestDisplayStageLabel;
    const latestSeverity = trace?.latestSeverity;
    const startedOn = trace?.startedOn;
    const lastOccurredOn = trace?.lastOccurredOn;
    const entryCount = trace?.entryCount;
    const lastSequence = trace?.lastSequence;
    const completedOn = readNullableTimestamp(trace?.completedOn);
    if (
      !thoughtTraceId ||
      !sessionId ||
      !workspaceId ||
      !streamId ||
      !isWebChatRealtimeAudienceScope(audienceScope) ||
      !isWebChatRenderEventStage(latestStage) ||
      !isWebChatRealtimeRenderState(latestState) ||
      !isWebChatRenderEventType(latestEventType) ||
      !latestMessage ||
      !isWebChatRenderDisplayKind(latestDisplayKind) ||
      !isWebChatRenderDisplayStageLabel(latestDisplayStageLabel) ||
      !isWebChatRealtimeRenderSeverity(latestSeverity) ||
      !isCanonicalTimestamp(startedOn) ||
      !isCanonicalTimestamp(lastOccurredOn) ||
      !isNonNegativeSafeInteger(entryCount) ||
      !isPositiveSafeInteger(lastSequence) ||
      typeof trace?.latestPromoteToFinalMessage !== 'boolean' ||
      completedOn === undefined ||
      requestedByUserId === undefined ||
      workRequestId === undefined ||
      correlationId === undefined
    ) {
      return null;
    }

    if (
      !hasValidHistoricalSystemNoticeTuple(
        latestEventType,
        latestStage,
        latestState,
        latestDisplayKind,
        latestDisplayStageLabel,
      )
    ) {
      return null;
    }

    const isTerminal = this.isTerminalThoughtTraceSummary(
      latestEventType,
      latestStage,
      latestState,
    );

    return {
      thoughtTraceId,
      sessionId,
      workspaceId,
      requestedByUserId,
      workRequestId,
      correlationId,
      streamId,
      audienceScope,
      latestEventType,
      latestStage,
      latestState,
      latestMessage,
      latestDisplayKind,
      latestDisplayStageLabel,
      latestPromoteToFinalMessage: trace.latestPromoteToFinalMessage,
      latestSeverity,
      entryCount,
      lastSequence,
      startedOn,
      lastOccurredOn,
      completedOn,
      isActive: !completedOn && !isTerminal,
    };
  }

  normalizeThoughtTraceEntry(
    entry: ChatSessionThoughtTraceEntryResponse | null | undefined,
  ): WebChatCanonicalRenderEvent | null {
    const sequence = entry?.sequence;
    const thoughtTraceId = readRequiredUuid(entry?.thoughtTraceId);
    const streamId = readExactNonemptyString(entry?.streamId);
    const audienceScope = entry?.audienceScope;
    const workspaceId = readRequiredUuid(entry?.workspaceId);
    const requestedByUserId = readNullableUuid(entry?.requestedByUserId);
    const sessionId = readNullableUuid(entry?.sessionId);
    const workRequestId = readNullableUuid(entry?.workRequestId);
    const correlationId = readNullableUuid(entry?.correlationId);
    const sourceSurface = readExactNonemptyString(entry?.sourceSurface);
    const eventType = entry?.eventType;
    const stage = entry?.stage;
    const state = entry?.state;
    const message = readExactNonemptyString(entry?.message);
    const displayKind = entry?.displayKind;
    const displayStageLabel = entry?.displayStageLabel;
    const severity = entry?.severity;
    const occurredOn = entry?.occurredOn;
    if (
      !isPositiveSafeInteger(sequence) ||
      !thoughtTraceId ||
      !streamId ||
      !isWebChatRealtimeAudienceScope(audienceScope) ||
      !workspaceId ||
      !sourceSurface ||
      !isWebChatRenderEventType(eventType) ||
      !isWebChatRenderEventStage(stage) ||
      !isWebChatRealtimeRenderState(state) ||
      !message ||
      !isWebChatRenderDisplayKind(displayKind) ||
      !isWebChatRenderDisplayStageLabel(displayStageLabel) ||
      !isWebChatRealtimeRenderSeverity(severity) ||
      !isCanonicalTimestamp(occurredOn) ||
      requestedByUserId === undefined ||
      sessionId === undefined ||
      workRequestId === undefined ||
      correlationId === undefined ||
      typeof entry?.promoteToFinalMessage !== 'boolean'
    ) {
      return null;
    }

    const data = this.resolveThoughtTraceRenderData(entry);
    if (data === undefined) {
      return null;
    }

    const decodedPulse = decodeWebChatPulseMetadata(data);
    const decodedPresentations = decodeWebChatPresentations(data);
    const compactionNotice =
      eventType === RealtimeRenderEventType.ConversationSystemNoticeCompaction
        ? decodeConversationCompactionSystemNoticePayload(data)
        : null;
    if (
      !decodedPulse.isValid ||
      !decodedPresentations.isValid ||
      decodedPresentations.presentations.some(
        (presentation) => presentation.workspaceId !== workspaceId,
      ) ||
      !hasValidHistoricalCompactionContract(
        eventType,
        stage,
        state,
        displayKind,
        displayStageLabel,
        workspaceId,
        sessionId,
        requestedByUserId,
        correlationId,
        compactionNotice,
      )
    ) {
      return null;
    }

    return {
      streamId,
      audienceScope,
      eventType,
      sequence,
      checkpoint: String(sequence),
      workspaceId,
      requestedByUserId,
      sessionId,
      threadId: null,
      workRequestId,
      correlationId,
      sourceSurface,
      stage,
      state,
      message,
      displayKind,
      displayStageLabel,
      promoteToFinalMessage: entry.promoteToFinalMessage,
      severity,
      occurredOn,
      presentations: [...decodedPresentations.presentations],
      pulse: decodedPulse.pulse,
      data,
      compactionNotice,
    };
  }

  resolveThoughtTraceRenderData(
    entry: ChatSessionThoughtTraceEntryResponse | null | undefined,
  ): JsonValue | undefined {
    let restored: JsonValue = null;
    if (entry?.data !== null && entry?.data !== undefined) {
      const decoded = decodeThoughtTraceDataSnapshot(entry.data);
      if (!decoded.isValid) {
        return undefined;
      }
      restored = decoded.value;
    }

    if (
      entry?.eventType ===
      RealtimeRenderEventType.ConversationSystemNoticeCompaction
    ) {
      return restored;
    }

    const progress = entry?.conversationProgressData;
    if (!progress) {
      return restored;
    }

    const merged = mergeThoughtTraceProgressData(restored, progress);
    return merged.isValid ? merged.value : undefined;
  }

  upsertThoughtTraceFromRenderEvent(
    currentState: WebChatState,
    renderEvent: WebChatCanonicalRenderEvent,
  ): {
    thoughtTraces: WebChatThoughtTraceSummary[];
    thoughtTraceEntriesById: Record<string, WebChatCanonicalRenderEvent[]>;
    activeThoughtTraceId: string | null;
    activeThoughtEntries: WebChatRenderEvent[];
    isThoughtChainActive: boolean;
  } {
    const traces = [...currentState.thoughtTraces];
    const traceEntriesById = { ...currentState.thoughtTraceEntriesById };
    const traceIndex = this.findThoughtTraceIndexForRenderEvent(
      traces,
      renderEvent,
    );
    const normalizedOccurredOn = renderEvent.occurredOn;
    const isTerminal = this.isTerminalThoughtTraceEvent(renderEvent);
    const shouldRecordEntry =
      this.messages.isThoughtLikeRenderEvent(renderEvent);

    if (traceIndex < 0 && !shouldRecordEntry) {
      return {
        thoughtTraces: this.sortThoughtTracesChronologically(traces),
        thoughtTraceEntriesById: traceEntriesById,
        activeThoughtTraceId: currentState.activeThoughtTraceId,
        activeThoughtEntries: currentState.thoughtChain,
        isThoughtChainActive: currentState.isThoughtChainActive,
      };
    }

    let nextTrace: WebChatThoughtTraceSummary;
    if (traceIndex >= 0) {
      const existing = traces[traceIndex];
      const advancesSummary = this.shouldAdvanceThoughtTraceSummary(
        existing,
        renderEvent,
        normalizedOccurredOn,
      );
      nextTrace = {
        ...existing,
        requestedByUserId:
          existing.requestedByUserId ?? renderEvent.requestedByUserId,
        workRequestId: existing.workRequestId ?? renderEvent.workRequestId,
        correlationId: existing.correlationId ?? renderEvent.correlationId,
        latestEventType: advancesSummary
          ? renderEvent.eventType
          : existing.latestEventType,
        latestStage: advancesSummary ? renderEvent.stage : existing.latestStage,
        latestState: advancesSummary ? renderEvent.state : existing.latestState,
        latestMessage: advancesSummary
          ? renderEvent.message
          : existing.latestMessage,
        latestDisplayKind: advancesSummary
          ? renderEvent.displayKind
          : existing.latestDisplayKind,
        latestDisplayStageLabel: advancesSummary
          ? renderEvent.displayStageLabel
          : existing.latestDisplayStageLabel,
        latestPromoteToFinalMessage: advancesSummary
          ? renderEvent.promoteToFinalMessage
          : existing.latestPromoteToFinalMessage,
        latestSeverity: advancesSummary
          ? renderEvent.severity
          : existing.latestSeverity,
        entryCount: existing.entryCount,
        lastSequence: Math.max(existing.lastSequence, renderEvent.sequence),
        lastOccurredOn: this.maxOccurredOn(
          existing.lastOccurredOn,
          normalizedOccurredOn,
        ),
        completedOn: advancesSummary
          ? isTerminal
            ? normalizedOccurredOn
            : null
          : existing.completedOn,
        isActive: advancesSummary ? !isTerminal : existing.isActive,
      };
      traces[traceIndex] = nextTrace;
    } else {
      nextTrace = {
        thoughtTraceId: this.messages.buildLiveThoughtTraceId(renderEvent),
        sessionId:
          renderEvent.sessionId ??
          this.state.stateSubject.value.session?.sessionId ??
          '',
        workspaceId: renderEvent.workspaceId,
        requestedByUserId: renderEvent.requestedByUserId,
        workRequestId: renderEvent.workRequestId,
        correlationId: renderEvent.correlationId,
        streamId: renderEvent.streamId,
        audienceScope: renderEvent.audienceScope,
        latestEventType: renderEvent.eventType,
        latestStage: renderEvent.stage,
        latestState: renderEvent.state,
        latestMessage: renderEvent.message,
        latestDisplayKind: renderEvent.displayKind,
        latestDisplayStageLabel: renderEvent.displayStageLabel,
        latestPromoteToFinalMessage: renderEvent.promoteToFinalMessage,
        latestSeverity: renderEvent.severity,
        entryCount: 0,
        lastSequence: renderEvent.sequence,
        startedOn: normalizedOccurredOn,
        lastOccurredOn: normalizedOccurredOn,
        completedOn: isTerminal ? normalizedOccurredOn : null,
        isActive: !isTerminal,
      };
      traces.push(nextTrace);
    }

    const mapKey = this.buildThoughtTraceMapKey(nextTrace.thoughtTraceId);
    const existingEntries = traceEntriesById[mapKey] ?? [];
    const mergedEntries = this.mergeThoughtTraceEntries(
      existingEntries,
      renderEvent,
      shouldRecordEntry,
    );
    traceEntriesById[mapKey] = mergedEntries;

    const updatedTrace = {
      ...nextTrace,
      entryCount: Math.max(nextTrace.entryCount, mergedEntries.length),
    };
    const updatedTraceIndex = traces.findIndex(
      (trace) => trace.thoughtTraceId === updatedTrace.thoughtTraceId,
    );
    if (updatedTraceIndex >= 0) {
      traces[updatedTraceIndex] = updatedTrace;
    }

    const activeThoughtTraceId = updatedTrace.isActive
      ? updatedTrace.thoughtTraceId
      : null;

    return {
      thoughtTraces: this.sortThoughtTracesChronologically(traces),
      thoughtTraceEntriesById: traceEntriesById,
      activeThoughtTraceId,
      activeThoughtEntries: activeThoughtTraceId
        ? (traceEntriesById[
            this.buildThoughtTraceMapKey(activeThoughtTraceId)
          ] ?? [])
        : [],
      isThoughtChainActive: activeThoughtTraceId != null,
    };
  }

  findThoughtTraceIndexForRenderEvent(
    traces: WebChatThoughtTraceSummary[],
    renderEvent: WebChatCanonicalRenderEvent,
  ): number {
    const liveThoughtTraceId =
      this.messages.buildLiveThoughtTraceId(renderEvent);
    const liveTraceIndex = traces.findIndex(
      (trace) => trace.thoughtTraceId === liveThoughtTraceId,
    );
    if (liveTraceIndex >= 0) {
      return liveTraceIndex;
    }

    const replayMatchIndex = traces.findIndex(
      (trace) =>
        renderEvent.sequence <= trace.lastSequence &&
        this.maxOccurredOn(trace.startedOn, renderEvent.occurredOn) ===
          renderEvent.occurredOn &&
        this.matchesThoughtTraceIdentity(trace, renderEvent),
    );
    if (replayMatchIndex >= 0) {
      return replayMatchIndex;
    }

    return traces.findIndex((trace) =>
      this.canAppendRenderEventToThoughtTrace(trace, renderEvent),
    );
  }

  canAppendRenderEventToThoughtTrace(
    trace: WebChatThoughtTraceSummary,
    renderEvent: WebChatCanonicalRenderEvent,
  ): boolean {
    return (
      trace.isActive && this.matchesThoughtTraceIdentity(trace, renderEvent)
    );
  }

  matchesThoughtTraceIdentity(
    trace: WebChatThoughtTraceSummary,
    renderEvent: WebChatCanonicalRenderEvent,
  ): boolean {
    if (trace.correlationId && renderEvent.correlationId) {
      return trace.correlationId === renderEvent.correlationId;
    }

    if (trace.workRequestId && renderEvent.workRequestId) {
      return trace.workRequestId === renderEvent.workRequestId;
    }

    return trace.streamId === renderEvent.streamId;
  }

  isTerminalThoughtTraceEvent(
    renderEvent: WebChatCanonicalRenderEvent,
  ): boolean {
    return (
      renderEvent.eventType ===
        RealtimeRenderEventType.ConversationResponseCompleted ||
      isTerminalThoughtStatus(renderEvent.stage, renderEvent.state)
    );
  }

  isTerminalThoughtTraceSummary(
    eventType: RealtimeRenderEventType,
    stage: RealtimeRenderEventStage,
    state: RealtimeRenderState,
  ): boolean {
    return (
      eventType === RealtimeRenderEventType.ConversationResponseCompleted ||
      isTerminalThoughtStatus(stage, state)
    );
  }

  buildThoughtTraceMapKey(thoughtTraceId: string): string {
    return thoughtTraceId;
  }

  async loadThoughtTraceEntriesAsync(
    summary: WebChatThoughtTraceSummary,
  ): Promise<void> {
    const sessionId = this.state.stateSubject.value.session?.sessionId;
    const normalizedThoughtTraceId = this.messages.normalizeThoughtTraceId(
      summary.thoughtTraceId,
    );
    if (!sessionId || !normalizedThoughtTraceId) {
      return;
    }

    const mapKey = this.buildThoughtTraceMapKey(normalizedThoughtTraceId);
    const currentState = this.state.stateSubject.value;
    if (
      currentState.thoughtTraceEntriesById[mapKey] ||
      currentState.loadingThoughtTraceIds.includes(normalizedThoughtTraceId)
    ) {
      return;
    }

    this.state.patch({
      loadingThoughtTraceIds: [
        ...currentState.loadingThoughtTraceIds,
        normalizedThoughtTraceId,
      ],
    });

    try {
      const detail = await firstValueFrom(
        this.state.chatClient.resolveSessionThoughtTrace({
          sessionId,
          body: {
            thoughtTraceId: this.messages.isGuid(normalizedThoughtTraceId)
              ? normalizedThoughtTraceId
              : null,
            workRequestId: summary.workRequestId,
            correlationId: summary.correlationId,
            streamId: summary.streamId,
            startedOn: summary.startedOn,
            lastSequence: summary.lastSequence,
          },
        }),
      );

      const normalizedSummary = this.normalizeThoughtTraceSummary(
        detail?.summary,
      );
      const normalizedEntries = (detail?.entries ?? [])
        .map((entry) => this.normalizeThoughtTraceEntry(entry))
        .filter(
          (entry): entry is WebChatCanonicalRenderEvent => entry !== null,
        );
      const compactedEntries =
        this.compactThoughtTraceEntries(normalizedEntries);
      const nextState = this.state.stateSubject.value;
      const resolvedThoughtTraceId =
        normalizedSummary?.thoughtTraceId ?? normalizedThoughtTraceId;
      const resolvedMapKey = this.buildThoughtTraceMapKey(
        resolvedThoughtTraceId,
      );
      this.state.patch({
        thoughtTraces: normalizedSummary
          ? this.sortThoughtTracesChronologically(
              nextState.thoughtTraces.map((trace) =>
                this.buildThoughtTraceMapKey(trace.thoughtTraceId) === mapKey
                  ? normalizedSummary
                  : trace,
              ),
            )
          : nextState.thoughtTraces,
        thoughtTraceEntriesById: {
          ...nextState.thoughtTraceEntriesById,
          [mapKey]: compactedEntries,
          [resolvedMapKey]: compactedEntries,
        },
      });
    } catch {
      return;
    } finally {
      const finalState = this.state.stateSubject.value;
      this.state.patch({
        loadingThoughtTraceIds: finalState.loadingThoughtTraceIds.filter(
          (id) => id !== normalizedThoughtTraceId,
        ),
      });
    }
  }

  sortThoughtTracesChronologically(
    traces: WebChatThoughtTraceSummary[],
  ): WebChatThoughtTraceSummary[] {
    return [...traces].sort((left, right) => {
      const leftTime = Date.parse(left.startedOn) || 0;
      const rightTime = Date.parse(right.startedOn) || 0;
      if (leftTime !== rightTime) {
        return leftTime - rightTime;
      }

      const leftLastOccurred =
        Date.parse(left.lastOccurredOn ?? left.startedOn) || 0;
      const rightLastOccurred =
        Date.parse(right.lastOccurredOn ?? right.startedOn) || 0;
      if (leftLastOccurred !== rightLastOccurred) {
        return leftLastOccurred - rightLastOccurred;
      }

      if (left.lastSequence !== right.lastSequence) {
        return left.lastSequence - right.lastSequence;
      }

      return left.thoughtTraceId.localeCompare(right.thoughtTraceId);
    });
  }

  maxOccurredOn(left: string, right: string): string {
    const leftTime = Date.parse(left) || 0;
    const rightTime = Date.parse(right) || 0;
    return rightTime >= leftTime ? right : left;
  }

  private shouldAdvanceThoughtTraceSummary(
    trace: WebChatThoughtTraceSummary,
    renderEvent: WebChatCanonicalRenderEvent,
    occurredOn: string,
  ): boolean {
    if (renderEvent.sequence > trace.lastSequence) {
      return true;
    }

    if (renderEvent.sequence < trace.lastSequence) {
      return false;
    }

    return this.maxOccurredOn(trace.lastOccurredOn, occurredOn) === occurredOn;
  }

  private compactThoughtTraceEntries(
    entries: WebChatCanonicalRenderEvent[],
  ): WebChatCanonicalRenderEvent[] {
    return entries
      .slice()
      .sort((left, right) => left.sequence - right.sequence)
      .reduce<WebChatCanonicalRenderEvent[]>(
        (result, entry) => this.mergeThoughtTraceEntries(result, entry, true),
        [],
      );
  }

  private mergeThoughtTraceEntries(
    existingEntries: WebChatCanonicalRenderEvent[],
    renderEvent: WebChatCanonicalRenderEvent,
    shouldRecordEntry: boolean,
  ): WebChatCanonicalRenderEvent[] {
    if (
      !shouldRecordEntry ||
      existingEntries.some((entry) => entry.sequence === renderEvent.sequence)
    ) {
      return existingEntries;
    }

    if (!this.isFallbackPulseRenderEvent(renderEvent)) {
      return [...existingEntries, renderEvent].sort(
        (left, right) => left.sequence - right.sequence,
      );
    }

    const fallbackIndex = this.findFallbackPulseCompactionIndex(
      existingEntries,
      renderEvent,
    );
    if (fallbackIndex < 0) {
      return [...existingEntries, renderEvent].sort(
        (left, right) => left.sequence - right.sequence,
      );
    }

    const existingFallback = existingEntries[fallbackIndex];
    if (existingFallback.sequence > renderEvent.sequence) {
      return existingEntries;
    }

    return existingEntries
      .map((entry, index) => (index === fallbackIndex ? renderEvent : entry))
      .sort((left, right) => left.sequence - right.sequence);
  }

  private findFallbackPulseCompactionIndex(
    existingEntries: WebChatCanonicalRenderEvent[],
    renderEvent: WebChatCanonicalRenderEvent,
  ): number {
    for (let index = existingEntries.length - 1; index >= 0; index -= 1) {
      const entry = existingEntries[index];
      if (
        this.isFallbackPulseRenderEvent(entry) &&
        this.matchesPulseCompactionIdentity(entry, renderEvent)
      ) {
        return index;
      }
    }

    return -1;
  }

  private isFallbackPulseRenderEvent(
    renderEvent: WebChatCanonicalRenderEvent,
  ): boolean {
    return (
      renderEvent.pulse.isFallback ||
      renderEvent.pulse.pulseKind ===
        ConversationPulseKind.DeterministicKeepAlive
    );
  }

  private matchesPulseCompactionIdentity(
    left: WebChatCanonicalRenderEvent,
    right: WebChatCanonicalRenderEvent,
  ): boolean {
    return (
      this.normalizePulseIdentityPart(left.streamId) ===
        this.normalizePulseIdentityPart(right.streamId) &&
      left.stage === right.stage &&
      this.normalizePulseIdentityPart(
        left.pulse.pulseSource ?? left.sourceSurface,
      ) ===
        this.normalizePulseIdentityPart(
          right.pulse.pulseSource ?? right.sourceSurface,
        ) &&
      this.normalizePulseIdentityPart(left.pulse.messageKey) ===
        this.normalizePulseIdentityPart(right.pulse.messageKey) &&
      this.normalizePulseIdentityPart(left.pulse.operationKey) ===
        this.normalizePulseIdentityPart(right.pulse.operationKey) &&
      this.normalizePulseIdentityPart(left.pulse.instructionId) ===
        this.normalizePulseIdentityPart(right.pulse.instructionId)
    );
  }

  private normalizePulseIdentityPart(value: string | null | undefined): string {
    return value ?? '';
  }
}

function isPositiveSafeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}

function isNonNegativeSafeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

function readRequiredUuid(value: unknown): string | null {
  return isCanonicalUuid(value) ? value : null;
}

function readNullableUuid(value: unknown): string | null | undefined {
  return value === null ? null : isCanonicalUuid(value) ? value : undefined;
}

function readExactNonemptyString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 && value === value.trim()
    ? value
    : null;
}

function readNullableTimestamp(value: unknown): string | null | undefined {
  return value === null
    ? null
    : isCanonicalTimestamp(value)
      ? value
      : undefined;
}

function isTerminalThoughtStatus(
  stage: RealtimeRenderEventStage,
  state: RealtimeRenderState,
): boolean {
  return (
    state === RealtimeRenderState.Failed ||
    state === RealtimeRenderState.PrimitiveFailed ||
    state === RealtimeRenderState.Waiting ||
    state === RealtimeRenderState.NeedsInput ||
    state === RealtimeRenderState.Canceled ||
    state === RealtimeRenderState.Cancelled ||
    state === RealtimeRenderState.PrimitiveCancelled ||
    state === RealtimeRenderState.Disconnected ||
    stage === ConversationPulseStage.Terminal ||
    ((stage === RealtimeRenderStage.Response ||
      stage === RealtimeRenderStage.Command) &&
      state === RealtimeRenderState.Completed) ||
    stage === RealtimeRenderStage.Requirements ||
    stage === RealtimeRenderStage.Clarification ||
    stage === RealtimeRenderStage.Approval
  );
}

function hasValidHistoricalCompactionContract(
  eventType: RealtimeRenderEventType,
  stage: RealtimeRenderEventStage,
  state: RealtimeRenderState,
  displayKind: RealtimeRenderDisplayKind,
  displayStageLabel: RealtimeRenderDisplayStageLabel,
  workspaceId: string,
  sessionId: string | null,
  requestedByUserId: string | null,
  correlationId: string | null,
  notice: ReturnType<typeof decodeConversationCompactionSystemNoticePayload>,
): boolean {
  const isCompaction =
    eventType === RealtimeRenderEventType.ConversationSystemNoticeCompaction;
  if (
    !hasValidHistoricalSystemNoticeTuple(
      eventType,
      stage,
      state,
      displayKind,
      displayStageLabel,
    )
  ) {
    return false;
  }
  return isCompaction
    ? notice !== null &&
        notice.workspaceId === workspaceId &&
        notice.sessionId === sessionId &&
        notice.userId === requestedByUserId &&
        notice.correlationId === correlationId
    : notice === null;
}

function hasValidHistoricalSystemNoticeTuple(
  eventType: RealtimeRenderEventType,
  stage: RealtimeRenderEventStage,
  state: RealtimeRenderState,
  displayKind: RealtimeRenderDisplayKind,
  displayStageLabel: RealtimeRenderDisplayStageLabel,
): boolean {
  const hasSystemDiscriminant =
    stage === RealtimeRenderStage.SystemNotice ||
    displayKind === RealtimeRenderDisplayKind.SystemNotice ||
    displayStageLabel === RealtimeRenderDisplayStageLabel.System;
  return eventType ===
    RealtimeRenderEventType.ConversationSystemNoticeCompaction
    ? stage === RealtimeRenderStage.SystemNotice &&
        state === RealtimeRenderState.Completed &&
        displayKind === RealtimeRenderDisplayKind.SystemNotice &&
        displayStageLabel === RealtimeRenderDisplayStageLabel.System
    : !hasSystemDiscriminant;
}
